// src/pages/CalendarGeneral.jsx
import React, { useEffect, useState } from "react";
import styles from "./CalendarGeneral.module.css";
import api from "../api";

import EventCreateModal from "../components/EventCreateModal";
import EventDetailModal from "../components/EventDetailModal";

export default function CalendarGeneral() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [serverEvents, setServerEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 모달 상태
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  function monthRange(dateObj) {
    const y = dateObj.getFullYear();
    const m = dateObj.getMonth();
    const start = new Date(y, m, 1);
    const end = new Date(y, m + 1, 0);
    const iso = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;
    return { from: iso(start), to: iso(end) };
  }

  const loadEvents = async () => {
    setLoading(true);
    setError("");
    try {
      const { from, to } = monthRange(currentDate);
      const { data } = await api.get("/calendar/events/", {
        params: { start_date__lte: to, end_date__gte: from },
      });
      const list = Array.isArray(data) ? data : data?.results ?? [];
      setServerEvents(list);
      // console.log("[CAL] fetched events:", list?.length ?? 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "캘린더 불러오기 실패");
      setServerEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [currentDate]);

  function getEventsForDate(dateObj) {
    const iso = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;
    const day = iso(dateObj);

    return (serverEvents || [])
      .filter((ev) => ev.start_date <= day && day <= ev.end_date)
      .map((ev) => ({
        ...ev,
        isAllDay: ev.is_all_day,
        startTime: ev.start_time,
        endTime: ev.end_time,
      }));
  }

  const openDetailModal = (event) => {
    setSelectedEvent(event);
    setShowDetailModal(true);
  };

  // ─ 렌더 함수들 ─
  const renderMonthDayEvents = (date) => {
    const dayEvents = getEventsForDate(date);
    const maxEvents = 3;

    return dayEvents.slice(0, maxEvents).map((event) => (
      <div
        key={event.id}
        className={`${styles.monthEvent} ${styles[`eventType${event.event_type || event.type}`]}`}
        onClick={(e) => {
          e.stopPropagation();
          openDetailModal(event);
        }}
      >
        <div className={styles.eventAuthor}>
          {event.author_name || event.author} {event.author_position ?? "직급"}
        </div>
        <div className={styles.eventTime}>
          {event.is_all_day || event.isAllDay
            ? "하루 종일"
            : `${event.start_time || event.startTime} - ${event.end_time || event.endTime}`}
        </div>
        <div className={styles.eventTitle}>{event.title}</div>
      </div>
    ));
  };

  const renderEventsForHour = (hour, targetDate = null) => {
    const dayEvents = getEventsForDate(targetDate || currentDate);

    return dayEvents
      .map((event) => {
        if (event.isAllDay || event.is_all_day) {
          if (hour === 6) {
            return (
              <div
                key={event.id}
                className={`${styles.eventCard} ${styles.allDayEvent}`}
                onClick={() => openDetailModal(event)}
              >
                <div className={styles.eventAuthor}>
                  {event.author_name ?? event.author} {event.author_position ?? "직급"}
                </div>
                <div className={styles.eventTitle}>{event.title}</div>
                <div className={styles.eventTime}>하루 종일</div>
                <div className={styles.eventType}>{event.event_type}</div>
              </div>
            );
          }
          return null;
        }

        const startTime = event.start_time || event.startTime;
        const endTime = event.end_time || event.endTime;
        if (!startTime) return null;

        const startHour = parseInt(startTime.split(":")[0], 10);
        let endHour = endTime ? parseInt(endTime.split(":")[0], 10) : startHour;
        if (endTime === "24:00" || endTime === "00:00") endHour = 24;
        if (!endTime || endHour <= startHour) endHour = Math.min(startHour + 1, 24);

        if (hour >= startHour && hour < endHour) {
          const isFirstHour = hour === startHour;
          const duration = endHour - startHour;

          if (isFirstHour) {
            return (
              <div
                key={event.id}
                className={`${styles.eventCard} ${styles.timedEvent} ${styles.eventStart}`}
                style={{ height: `${duration * 60}px`, position: "absolute", top: "0", zIndex: 1 }}
                onClick={() => openDetailModal(event)}
              >
                <div className={styles.eventAuthor}>
                  {event.author_name ?? event.author} {event.author_position ?? "직급"}
                </div>
                <div className={styles.eventTitle}>{event.title}</div>
                <div className={styles.eventTime}>
                  {startTime} - {endTime}
                </div>
                <div className={styles.eventType}>{event.event_type}</div>
              </div>
            );
          }

          return (
            <div
              key={`${event.id}-continue-${hour}`}
              className={`${styles.eventCard} ${styles.eventContinue}`}
              style={{ height: "100%", minHeight: "40px" }}
            />
          );
        }
        return null;
      })
      .filter(Boolean);
  };

  return (
    <div className={styles.wrap}>
      {loading && <div className={styles.debug}>이벤트 불러오는 중…</div>}
      {error && <div className={styles.error} style={{ color: "red" }}>{error}</div>}

      <div className={styles.header}>
        <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}>◀</button>
        <div className={styles.monthTitle}>
          {currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월
        </div>
        <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}>▶</button>
        <button onClick={() => setShowCreateModal(true)} className={styles.addButton}>+ 새 일정</button>
      </div>

      {/* 모달 */}
      <EventCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => loadEvents()} // 저장 후 새로고침
      />

      <EventDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        event={selectedEvent}
        onUpdate={async () => {
          await loadEvents();
        }}
        onDelete={async () => {
          await loadEvents();
        }}
      />
    </div>
  );
}
